import { baseApiService, type QueryParams } from './base'
import * as fileplanApi from './fileplan'

const BASE_URL = '/api/v1/etmf/packages'

// Types for packages API
export interface Package {
  id: number
  name: string
  description?: string
  level?: string
  filePlanName?: string
  createdAt?: number
  updatedAt?: number
  triggerDetails?: {
    triggerDetail?: {
      attributeName?: string
    }
  }
  [key: string]: any
}

export interface CTMSMilestonesResponse {
  ctmsMilestone: unknown[]
}

export interface PackageListResponse {
  elements: Package[]
  totalElements: number
  hasNextPage?: boolean
}

export interface PackageOption {
  id: string
  label: string
  value: string
}

export interface PackageOptionsResponse {
  options: PackageOption[]
}

export interface TriggerListResponse {
  triggers: string[]
}

export interface TeamRolesResponse {
  teamMembers: string[]
}

export interface VisitTypeCategoryResponse {
  visitTypeCategories: string[]
}

export interface PropertiesIntersectedResponse {
  properties: Record<string, unknown>
}

// API Functions
export async function getPackage(id: string): Promise<Package> {
  return baseApiService.fetch<Package>(`${BASE_URL}/${id}`)
}

export async function createPackage(data: Partial<Package>): Promise<Package> {
  return baseApiService.fetch<Package>(BASE_URL, {
    method: 'POST',
    body: data
  })
}

export async function updatePackage(id: string, data: Partial<Package>): Promise<Package> {
  return baseApiService.fetch<Package>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    body: data
  })
}

export async function removePackage(id: string | number): Promise<void> {
  return baseApiService.fetch<void>(`${BASE_URL}/${id}`, {
    method: 'DELETE'
  })
}

export async function getPackagesList(params?: QueryParams): Promise<PackageListResponse> {
  return baseApiService.fetch<PackageListResponse>(BASE_URL, { params })
}

export async function getAllPackagesForPicker(): Promise<PackageListResponse> {
  const response = await baseApiService.fetch<Package[]>(`${BASE_URL}/getall`)
  // Transform array response to match PackageListResponse structure
  return {
    elements: response,
    totalElements: response.length,
    hasNextPage: false
  }
}

export async function getPackageEntries(id: string): Promise<PackageOptionsResponse> {
  return baseApiService.fetch<PackageOptionsResponse>(`${BASE_URL}/${id}/options`)
}

export async function getDetailsForPropertiesMultipleArtifacts(
  artifactIds: string[],
  docTypes: string[],
  level: string
): Promise<PropertiesIntersectedResponse> {
  return baseApiService.fetch<PropertiesIntersectedResponse>(`${BASE_URL}/${level}/propertiesIntersected`, {
    params: { artifactIds, docTypeIds: docTypes }
  })
}

export async function getDetailsForPropertiesMultipleArtifactsPost(
  artifactIds: string[],
  docTypes: string[],
  level: string
): Promise<PropertiesIntersectedResponse> {
  return baseApiService.fetch<PropertiesIntersectedResponse>(`${BASE_URL}/${level}/propertiesIntersected`, {
    method: 'POST',
    body: { artifactIds, docTypeIds: docTypes }
  })
}

export async function applyPackage(data: Record<string, unknown>): Promise<unknown> {
  return baseApiService.fetch(`${BASE_URL}/apply`, {
    method: 'POST',
    body: data
  })
}

export async function getTriggerList(): Promise<string[]> {
  return baseApiService.fetch<string[]>(`${BASE_URL}/getTriggerList`)
}

export async function getTeamRolesForTrigger(): Promise<TeamRolesResponse> {
  return baseApiService.fetch<TeamRolesResponse>(`${BASE_URL}/getTeamRoles`)
}

export async function getVisitTypeCategory(): Promise<VisitTypeCategoryResponse> {
  return baseApiService.fetch<VisitTypeCategoryResponse>(`${BASE_URL}/getVisitTypeCategory`)
}

export async function getCTMSMilestonesReachedForTrigger(level: string): Promise<CTMSMilestonesResponse> {
  return baseApiService.fetch<CTMSMilestonesResponse>(`${BASE_URL}/getCtmsMilestones`, {
    params: { level }
  })
}

/**
 * Strict migration of legacy get function from AngularJS api.packages.js
 * GET /api/v1/etmf/packages/:id
 * @param id - Package ID
 * @returns Promise<Package>
 */
export async function get(id: string): Promise<Package> {
  return baseApiService.fetch<Package>(`${BASE_URL}/${id}`)
}

export async function getAllForPicker(): Promise<PackageListResponse> {
  return baseApiService.fetch<PackageListResponse>(`${BASE_URL}/getall`)
}

/**
 * Delegate to fileplan API for getEntriesForSelector
 * This maintains the same API signature as legacy code
 */
export async function getEntriesForSelector(
  filePlanId: number | null | undefined,
  level: string | undefined,
  selectedIds?: number[],
  isClone?: boolean,
  isFilePlanChanged?: boolean,
  entryNames?: string[]
): Promise<any[]> {
  if (!filePlanId || !level) {
    return []
  }
  return fileplanApi.getEntriesForSelector(
    String(filePlanId),
    level,
    selectedIds?.map(String),
    isClone,
    isFilePlanChanged,
    entryNames
  )
}

/**
 * Delegate to fileplan API for getLevel3Entries
 * This maintains the same API signature as legacy code
 */
export function getLevel3Entries(entriesL1: any[], predicate?: (entry: any) => boolean): any[] {
  return fileplanApi.getLevel3Entries(entriesL1, predicate)
}
